import { useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { buildCanonicalSeoUrl, buildXDefaultSeoUrl } from '../utils/seoUrl';

const SEO_CONTENT = {
  en: {
    title: 'GitScope - Knowledge Management & Collaboration',
    description: 'Build your private knowledge base, collaborate with your team, or share resources with the world.',
  },
  zh: {
    title: 'GitScope - 知识管理协作平台',
    description: '构建私有知识库，与团队协作共建，向世界分享精选资源。',
  },
};

export function useSEO() {
  const { language } = useLanguage();

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (typeof window === 'undefined') return;

    const content = SEO_CONTENT[language] ?? SEO_CONTENT.en;

    const upsertMetaByName = (name: string, value: string) => {
      let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', value);
    };

    const upsertMetaByProperty = (property: string, value: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', value);
    };

    const upsertLink = (rel: string, key: string, keyValue: string, href: string) => {
      let link = document.querySelector(`link[rel="${rel}"][${key}="${keyValue}"]`) as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        link.setAttribute(key, keyValue);
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    };

    const upsertCanonical = (href: string) => {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    };

    document.title = content.title;

    upsertMetaByName('description', content.description);
    upsertMetaByProperty('og:title', content.title);
    upsertMetaByProperty('og:description', content.description);
    upsertMetaByName('twitter:title', content.title);
    upsertMetaByName('twitter:description', content.description);

    const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined)?.trim().replace(/\/+$/, '') || window.location.origin;
    const ogImageUrl = `${siteUrl}/og-image.svg`;

    upsertMetaByProperty('og:image', ogImageUrl);
    upsertMetaByProperty('og:image:type', 'image/svg+xml');
    upsertMetaByProperty('og:image:alt', content.title);
    upsertMetaByProperty('og:image:width', '1200');
    upsertMetaByProperty('og:image:height', '630');
    upsertMetaByName('twitter:card', 'summary_large_image');
    upsertMetaByName('twitter:image', ogImageUrl);
    upsertMetaByName('twitter:image:alt', content.title);

    const htmlLang = language === 'zh' ? 'zh-CN' : 'en';
    document.documentElement.setAttribute('lang', htmlLang);

    const locale = language === 'zh' ? 'zh_CN' : 'en_US';
    const alternateLocale = language === 'zh' ? 'en_US' : 'zh_CN';
    upsertMetaByProperty('og:locale', locale);
    upsertMetaByProperty('og:locale:alternate', alternateLocale);

    const canonicalUrl = buildCanonicalSeoUrl(window.location.href, language);
    upsertCanonical(canonicalUrl.toString());
    upsertMetaByProperty('og:url', canonicalUrl.toString());
    upsertMetaByName('twitter:url', canonicalUrl.toString());

    const enUrl = buildCanonicalSeoUrl(canonicalUrl, 'en');
    const zhUrl = buildCanonicalSeoUrl(canonicalUrl, 'zh');
    const xDefaultUrl = buildXDefaultSeoUrl(canonicalUrl);

    upsertLink('alternate', 'hreflang', 'en', enUrl.toString());
    upsertLink('alternate', 'hreflang', 'zh-CN', zhUrl.toString());
    upsertLink('alternate', 'hreflang', 'x-default', xDefaultUrl.toString());
  }, [language]);
}

export default useSEO;
